import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Sparkles, Settings2, X, Check, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';

interface ConsentSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieBanner() {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<ConsentSettings>({
    necessary: true,
    analytics: true,
    marketing: true,
  });

  // Expose a direct function on window as a bulletproof mechanism on every render
  if (typeof window !== 'undefined') {
    (window as any).openSolidCookieBanner = () => {
      console.log('CookieBanner: openSolidCookieBanner called from render scope');
      setIsVisible(true);
      setShowCustomize(true);
    };
  }

  useEffect(() => {
    const handleOpenBanner = () => {
      console.log('CookieBanner: open-cookie-banner event received');
      setIsVisible(true);
      setShowCustomize(true);
    };

    window.addEventListener('open-cookie-banner', handleOpenBanner);

    // Initial load check
    const storedConsent = localStorage.getItem('solid-cookie-consent');
    if (storedConsent) {
      try {
        const parsed = JSON.parse(storedConsent) as ConsentSettings;
        loadScripts(parsed);
        setPreferences(parsed);
        setIsVisible(false); // Hide on start since they already gave consent
      } catch (e) {
        setIsVisible(true); // If parsing fails, show banner
      }
    } else {
      // No consent stored, show banner
      setIsVisible(true);
    }

    return () => {
      window.removeEventListener('open-cookie-banner', handleOpenBanner);
      // Keep openSolidCookieBanner mounted to prevent race conditions during unmounts/remounts
    };
  }, []);

  const loadScripts = (settings: ConsentSettings) => {
    if (settings.analytics || settings.marketing) {
      if (!document.querySelector('script[src*="googletagmanager.com/gtag"]')) {
        const scriptGA = document.createElement('script');
        scriptGA.async = true;
        scriptGA.src = 'https://www.googletagmanager.com/gtag/js?id=G-8CB2Y08WZF';
        document.head.appendChild(scriptGA);

        const scriptGAConfig = document.createElement('script');
        scriptGAConfig.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-8CB2Y08WZF');
        `;
        document.head.appendChild(scriptGAConfig);
      }

      if (!document.querySelector('script[src*="clarity.ms"]')) {
        const scriptClarity = document.createElement('script');
        scriptClarity.type = 'text/javascript';
        scriptClarity.innerHTML = `
          (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "w2j5bp8bhv");
        `;
        document.head.appendChild(scriptClarity);
      }
    }
  };

  const saveConsent = (settings: ConsentSettings) => {
    localStorage.setItem('solid-cookie-consent', JSON.stringify(settings));
    loadScripts(settings);
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const allRejected = { necessary: true, analytics: false, marketing: false };
    saveConsent(allRejected);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (type: 'analytics' | 'marketing') => {
    setPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div 
          key="cookie-banner-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-x-0 bottom-6 sm:bottom-8 md:bottom-10 z-[99999] px-4 sm:px-6 md:px-8 flex justify-center pointer-events-none"
          id="cookie-banner-container"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto bg-[#f4f4f5] text-zinc-805 w-full max-w-sm sm:max-w-lg md:max-w-[540px] rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-zinc-200/90 p-5 sm:p-6 md:p-7 flex flex-col md:flex-row items-center justify-between gap-5 select-none"
          >
            <div className="flex-1 min-w-0">
              <p className="text-zinc-700 font-inter-display font-normal text-[13px] sm:text-[14px] leading-relaxed select-text pointer-events-auto">
                {language === 'PL' ? (
                  <>
                    Używamy plików cookies, aby zapewnić Ci najlepsze doświadczenia na naszej stronie. Klikając "Akceptuję", zgadzasz się na ich użycie zgodnie z naszą{' '}
                    <Link 
                      to="/polityka-prywatnosci" 
                      onClick={() => setIsVisible(false)} 
                      className="text-[#6456b7] hover:text-[#4f4299] font-semibold underline underline-offset-2 transition-colors inline-block pointer-events-auto"
                    >
                      Polityką Prywatności
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    We use cookies to ensure you get the best experience on our website. By clicking "Accept", you agree to their use in accordance with our{' '}
                    <Link 
                      to="/polityka-prywatnosci" 
                      onClick={() => setIsVisible(false)} 
                      className="text-[#6456b7] hover:text-[#4f4299] font-semibold underline underline-offset-2 transition-colors inline-block pointer-events-auto"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </>
                )}
              </p>
            </div>

            {/* Accent Button */}
            <button
              onClick={handleAcceptAll}
              className="bg-[#c5bbf5] hover:bg-[#b0a5e8] text-[#111319] hover:text-[#050608] font-inter-display font-semibold text-xs sm:text-[13px] tracking-wide py-2.5 px-6 rounded-xl active:scale-95 transition-all w-full md:w-auto text-center shrink-0 min-h-[42px] cursor-pointer flex items-center justify-center shadow-md shadow-[#c5bbf5]/10"
            >
              {language === 'PL' ? 'Akceptuję' : 'Accept'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
